/**
 * Student list — UX/UI Specification §9.4.
 *
 * A table on a desktop, stacked cards on a phone: the same component, because
 * two implementations of one screen drift.
 *
 * No package column and no attendance column, ever (Constitution §5).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateShort, formatValue } from '@pp/shared';
import { GENDER_LABELS, type StudentListResponse } from '../api/types.js';
import { useApiData } from '../hooks/useApiData.js';
import { useDebounced } from '../hooks/useDebounced.js';
import './StudentList.css';

export function StudentList() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'active' | 'archived'>('active');

  // 300ms so a fast typist makes one request, not eight.
  const debouncedSearch = useDebounced(search, 300);

  const path = useMemo(() => {
    const params = new URLSearchParams({ status });
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    return `/api/students?${params.toString()}`;
  }, [status, debouncedSearch]);

  const { data, error, loading } = useApiData<StudentListResponse>(path);
  const items = data?.items ?? [];

  return (
    <section className="students">
      <header className="students__head">
        <h1>Öğrenciler</h1>
        <Link to="/ogrenciler/yeni" className="btn btn--primary">
          Yeni Öğrenci
        </Link>
      </header>

      <div className="students__controls">
        <input
          type="search"
          className="students__search"
          placeholder="Öğrenci ara"
          aria-label="Öğrenci ara"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="students__filter" role="group" aria-label="Durum">
          <button
            type="button"
            className={status === 'active' ? 'chip chip--on' : 'chip'}
            onClick={() => setStatus('active')}
            aria-pressed={status === 'active'}
          >
            Aktif
          </button>
          <button
            type="button"
            className={status === 'archived' ? 'chip chip--on' : 'chip'}
            onClick={() => setStatus('archived')}
            aria-pressed={status === 'archived'}
          >
            Arşiv
          </button>
        </div>
      </div>

      {error && (
        <p className="students__error" role="alert">
          {error}
        </p>
      )}

      {loading && <p className="students__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && (
        <div className="students__empty">
          {debouncedSearch.trim() ? (
            <>
              <p>Aramanıza uygun öğrenci bulunamadı.</p>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setSearch('')}
              >
                Aramayı temizle
              </button>
            </>
          ) : status === 'archived' ? (
            <p>Arşivlenmiş öğrenci yok.</p>
          ) : (
            <>
              <p>Henüz öğrenci eklenmemiş.</p>
              <Link to="/ogrenciler/yeni" className="btn btn--primary">
                Yeni Öğrenci
              </Link>
            </>
          )}
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="students__table-wrap">
            <table className="students__table">
              <thead>
                <tr>
                  <th scope="col">Ad Soyad</th>
                  <th scope="col">Yaş</th>
                  <th scope="col">Cinsiyet</th>
                  <th scope="col">Boy</th>
                  <th scope="col">Kilo</th>
                  <th scope="col">BMI</th>
                  <th scope="col">Son Ölçüm</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id}>
                    <th scope="row">
                      <Link to={`/ogrenciler/${s.id}`}>
                        {s.firstName} {s.lastName}
                      </Link>
                    </th>
                    <td>{s.age ?? '—'}</td>
                    <td>{GENDER_LABELS[s.gender]}</td>
                    <td>{formatValue(s.heightCm, 'cm', 1)}</td>
                    <td>{formatValue(s.currentWeightKg, 'kg', 1)}</td>
                    <td>{s.bmi === null ? '—' : formatValue(s.bmi, '', 1)}</td>
                    <td>
                      {s.lastMeasurementDate
                        ? formatDateShort(s.lastMeasurementDate)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="students__cards">
            {items.map((s) => (
              <li key={s.id}>
                <Link to={`/ogrenciler/${s.id}`} className="students__card">
                  <span className="students__card-name">
                    {s.firstName} {s.lastName}
                  </span>
                  <span className="students__card-meta">
                    {s.age !== null && `${s.age} yaş · `}
                    {formatValue(s.heightCm, 'cm', 1)} ·{' '}
                    {formatValue(s.currentWeightKg, 'kg', 1)}
                    {s.bmi !== null && ` · BMI ${formatValue(s.bmi, '', 1)}`}
                  </span>
                  <span className="students__card-meta">
                    {s.lastMeasurementDate
                      ? `Son ölçüm ${formatDateShort(s.lastMeasurementDate)}`
                      : 'Henüz ölçüm yok'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <p className="students__muted">{data?.pagination.total} öğrenci</p>
        </>
      )}
    </section>
  );
}
